<template>
  <div :class="controlClasses" class="mds-form-control mds-input-text">
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <template v-if="isDisabledHintVisible">
          <button class="disable-hint-container" :id="`hint-${uid}`">
            <VIcon theme="outlined" material="question_mark" />
          </button>

          <VTooltip :target="`#hint-${uid}`">
            <ul>
              <li v-for="(reason, index) in disabledReasons" :key="index">
                {{ reason }}
              </li>
            </ul>
          </VTooltip>
        </template>

        <input
          :disabled="disabled"
          :id="uid"
          :ref="uid"
          :placeholder="placeholder"
          :value="maskData.masked"
          type="text"
          autocomplete="off"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
        />

        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <button @click="onClear" class="clear" type="button" v-if="showClear">
          <VIcon material="cancel" />
        </button>
      </div>

      <div class="messages-row" v-if="isErrorsVisible">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import MaskUtils    from "@/core/utils/MaskUtils";
import VBaseControl from "@/core/services/VBaseControl";

import VTooltip from "@/lib/components/VTooltip/VTooltip.vue";
import VIcon    from "@/lib/components/VIcon/VIcon.vue";

export default defineComponent({
  name: "VInputMask",

  components: { VIcon, VTooltip },

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /**
     * Маска. По умолчанию используются следующие токены:
     * '#': {pattern: /\d/}
     * 'X': {pattern: /[0-9a-zA-Z]/}
     * 'S': {pattern: /[a-zA-Z]/}
     * 'A': {pattern: /[a-zA-Z]/, transform: v => v.toLocaleUpperCase()}
     * 'a': {pattern: /[a-zA-Z]/, transform: v => v.toLocaleLowerCase()}
     * '!': {escape: true}
     */
    mask: {
      type:     String,
      required: true,
    },
    /** Возвращать данные с маской. По умолчанию возвращает без маски. */
    masked: {
      type:    Boolean,
      default: false,
    },
    /**
     * Возвращать полный объект маски вида:
     * {
     *   masked: '',     - строка значения с маской
     *   raw: '',        - строка значения без маски
     *   fullfilled: false - маска заполнена полностью
     * }
     */
    raw: {
      type:    Boolean,
      default: false,
    },
    /** Кастомные токены поверх дефолтных */
    tokens: {
      type:    Object,
      default: () => MaskUtils.DEFAULT_TOKENS,
    },
    /** v-model (старое имя value, оставлено для обратной совместимости) */
    value: {
      type:    [String, Object],
      default: null,
    },
    /** Кнопка очистки */
    clearable: {
      type:    Boolean,
      default: false,
    },
    /** Выравнивание текста */
    textAlign: {
      type:      String,
      default:   null,
      validator: (value: string) => this.$core.constants.TEXT_ALIGNS.includes(value),
    },
  },

  emits: ["blur", "focus", "input"],

  data() {
    return {
      // Свойство нужно, чтобы предотвратить лишние эмиты при повторных refresh.
      lastValue: null,
      maskData: {
        masked:     this.value,
        raw:        "",
        fullfilled: false,
      },
    };
  },

  computed: {
    element() {
      return this.$refs[this.uid];
    },

    bitMask() {
      return MaskUtils.getBitMask(this.mask, this.tokens);
    },

    getValue() {
      if (this.raw === true) return { ...this.maskData };
      return this.masked === true ? this.maskData.masked : this.maskData.raw;
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true) classes.push("clearable");
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.value !== null
        && this.value !== ''
        && this.disabled === false;
    },

    /** labelStyles переопределяем — у инпута исторически width, а не flex. */
    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      if (this.textAlign  !== null) result["text-align"] = this.textAlign;
      return result;
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },
  },

  watch: {
    value: {
      handler(newValue) {
        this.refresh(newValue);
        if (this.validation.isDirty === true) {
          this.validate();
        }
      },
      immediate: true,
    },
    masked() {
      this.refresh(this.maskData.masked);
    },
  },

  methods: {
    onClear() {
      this.maskData = {
        masked:     "",
        raw:        "",
        fullfilled: false,
      };
      this.lastValue = { ...this.maskData };
      this.$emit("input", this.getValue);
      this.$emit("blur", this.getValue);
    },

    onInput(event) {
      if (event.isTrusted === false || this.element === null) return;

      let value          = MaskUtils.deleteNotTokens(event.target.value, this.mask, this.tokens);
      let cursorPosition = this.element.selectionEnd;
      let skipChars      = 0;

      if (value.length > this.bitMask.length) {
        const empty = value.indexOf("_", cursorPosition - 1);
        if (empty > -1) {
          value = value.slice(0, empty) + value.slice(empty + 1);
        }
      }

      switch (event.inputType) {
        case "insertText":
          skipChars = MaskUtils.notTokensCount(cursorPosition - 1, this.mask, this.tokens);
          break;
        case "deleteContentForward":
          skipChars = MaskUtils.notTokensCount(cursorPosition, this.mask, this.tokens);
          break;
        case "deleteContentBackward":
          if (cursorPosition > 0) {
            skipChars = MaskUtils.notTokensCount(cursorPosition - 1, this.mask, this.tokens, false);
          }
          break;
      }

      cursorPosition += skipChars;
      this.refresh(value, "input");

      this.$nextTick().then(() => {
        this.element.setSelectionRange(cursorPosition, cursorPosition);
      });
    },

    onBlur() {
      // Раньше тут была проверка на !fullfilled — её назначение неизвестно,
      // выкинуто ранее. Если что-то отвалится — копать сюда.
      this.$emit("blur", this.getValue);
    },

    onFocus(event) {
      this.validation.markTouched();
      this.refresh(this.maskData.masked === null ? "" : this.maskData.masked);

      if (this.maskData.fullfilled === false) {
        /**
         * Можно было хранить позицию каретки в отдельной переменной, но
         * нужно ловить все refresh для её сброса. Пускай пока функцией —
         * вероятность ошибки ниже.
         */
        const cursorPosition = MaskUtils.getCurrentCursorPointer(
          this.maskData.masked,
          this.maskData.raw,
          this.bitMask,
        );
        setTimeout(() => {
          this.element.setSelectionRange(cursorPosition, cursorPosition);
        }, 0);
      }
      this.$emit("focus", event);
    },

    refresh(newValue, eventName) {
      this.maskData = MaskUtils.maskAll(newValue, this.mask, this.tokens);

      if (this.validation.isDirty === false && this.maskData.masked !== this.value) {
        this.validation.markDirty();
      }

      switch (eventName) {
        case "blur":
          if (this.$core.utils.isEqual(this.maskData, this.lastValue) === false) {
            this.lastValue = { ...this.maskData };
            this.$emit("input", this.getValue);
          }
          this.$emit("blur", this.getValue);
          break;
        case "input":
          if (this.$core.utils.isEqual(this.maskData, this.lastValue) === false) {
            this.lastValue = { ...this.maskData };
            this.$emit("input", this.getValue);
          }
          break;
      }
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.value);
    },
  },
});
</script>
